import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { 
  Clock, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Edit,
  Save,
  X,
  CheckCircle,
  XCircle,
  Instagram,
  Facebook,
  ExternalLink
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { WeeklyHours } from '@communitly/shared';
import { formatTime12, formatTime24 } from '@/utils/timeUtils';
import SidebarOpeningHoursForm from '@/components/business/SidebarOpeningHoursForm';

interface BusinessSidebarProps {
  business: any;
  className?: string;
  isSticky?: boolean;
  editingStates: {
    contact: boolean;
    location: boolean;
    openingHours: boolean;
  };
  onToggleEdit: (section: 'contact' | 'location' | 'openingHours') => void;
  onSave: (section: 'contact' | 'location' | 'openingHours', event?: React.FormEvent) => void;
  editedData: {
    contact_email: string;
    contact_phone: string;
    website: string;
    instagram_url: string;
    facebook_url: string;
    tiktok_url: string;
    address_line1: string;
    address_line2: string;
    city: string;
    county: string;
    postal_code: string;
    country: string;
  };
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  editedOpeningHours: WeeklyHours;
  onOpeningHoursChange: (hours: WeeklyHours) => void;
  timeDisplayFormat: '12' | '24';
}

const BusinessSidebar: React.FC<BusinessSidebarProps> = ({ 
  business, 
  className,
  isSticky = true,
  editingStates,
  onToggleEdit,
  onSave,
  editedData,
  onInputChange,
  editedOpeningHours,
  onOpeningHoursChange,
  timeDisplayFormat
}) => {
  const [isPhoneValid, setIsPhoneValid] = React.useState(true);

  // Check if currently open (simple 9-5 check for now)
  const currentTime = new Date();
  const currentHour = currentTime.getHours();
  const isOpenNow = currentHour >= 9 && currentHour < 17;

  // Helper function to format time based on display format
  const formatTime = (time: string): string => {
    if (!time) return '';
    
    // Use the utility functions from timeUtils for proper formatting
    if (timeDisplayFormat === '12') {
      return formatTime12(time);
    } else {
      return formatTime24(time);
    }
  };

  // Helper function to render structured opening hours
  const renderOpeningHours = (openingHours: WeeklyHours | null) => {
    if (!openingHours) {
      return <p className="text-sm text-muted-foreground">No opening hours provided.</p>;
    }

    const DAYS = [
      'monday', 'tuesday', 'wednesday', 'thursday', 
      'friday', 'saturday', 'sunday'
    ] as const;

    const DAY_NAMES = {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday'
    };

    return (
      <div className="space-y-2">
        {DAYS.map(day => {
          const dayHours = openingHours[day];
          if (!dayHours) return null;

          return (
            <div key={day} className="flex justify-between items-center text-sm py-1">
              <span className="font-medium text-foreground min-w-[80px]">
                {DAY_NAMES[day]}
              </span>
              {dayHours.closed ? (
                <Badge variant="secondary" className="text-xs px-2 py-1 bg-red-50 text-red-700 border-red-200">
                  Closed
                </Badge>
              ) : (
                <span className="text-foreground font-mono">
                  {formatTime(dayHours.open)} - {formatTime(dayHours.close)}
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Handle phone number change with PhoneInput validation
  const handlePhoneChange = (value: string | undefined) => {
    const syntheticEvent = {
      target: {
        name: 'contact_phone',
        value: value || ''
      }
    } as React.ChangeEvent<HTMLInputElement>;
    onInputChange(syntheticEvent);
  };

  // Handle form submission for contact section with validation
  const handleContactFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isPhoneValid) {
      return; // Prevent saving if phone is invalid
    }
    
    onSave('contact', e);
  };

  return (
    <div className={cn(
      "space-y-6",
      isSticky && "sticky top-6",
      className
    )}>
      {/* Opening Hours Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center">
              <Clock className="h-5 w-5 mr-2 text-blue-600" />
              Opening Hours
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge 
                variant={isOpenNow ? "default" : "secondary"}
                className={cn(
                  "text-xs",
                  isOpenNow ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-red-100 text-red-800 hover:bg-red-100"
                )}
              >
                {isOpenNow ? (
                  <>
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Open Now
                  </>
                ) : (
                  <>
                    <XCircle className="h-3 w-3 mr-1" />
                    Closed
                  </>
                )}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {editingStates.openingHours ? (
            <div className="space-y-4">
              <div className="max-h-96 overflow-y-auto pr-2">
                <SidebarOpeningHoursForm
                  openingHours={editedOpeningHours}
                  onUpdate={onOpeningHoursChange}
                  timeFormat={timeDisplayFormat}
                />
              </div>
              <div className="flex gap-2 pt-2 border-t">
                <Button 
                  size="sm" 
                  onClick={() => onSave('openingHours')}
                  className="flex-1"
                >
                  <Save className="h-3 w-3 mr-1" />
                  Save Hours
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => onToggleEdit('openingHours')}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {renderOpeningHours(business.opening_hours)}
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full" 
                onClick={() => onToggleEdit('openingHours')}
              >
                <Edit className="h-3 w-3 mr-2" />
                Edit Hours
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Address Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center">
            <MapPin className="h-5 w-5 mr-2 text-red-600" />
            Address
          </CardTitle>
        </CardHeader>
        <CardContent>
          {editingStates.location ? (
            <div className="space-y-3">
              <div>
                <Label htmlFor="address_line1" className="text-xs">Address Line 1</Label>
                <Input 
                  id="address_line1"
                  name="address_line1" 
                  value={editedData.address_line1} 
                  onChange={onInputChange} 
                  className="h-8 text-xs"
                  placeholder="123 Business Street"
                />
              </div>
              
              <div>
                <Label htmlFor="address_line2" className="text-xs">Address Line 2</Label>
                <Input 
                  id="address_line2"
                  name="address_line2" 
                  value={editedData.address_line2} 
                  onChange={onInputChange} 
                  className="h-8 text-xs"
                  placeholder="Suite 100"
                />
              </div>
              
              <div>
                <Label htmlFor="city" className="text-xs">City</Label>
                <Input 
                  id="city"
                  name="city" 
                  value={editedData.city} 
                  onChange={onInputChange} 
                  className="h-8 text-xs"
                  placeholder="London"
                />
              </div>
              
              <div>
                <Label htmlFor="county" className="text-xs">County</Label>
                <Input 
                  id="county"
                  name="county" 
                  value={editedData.county} 
                  onChange={onInputChange} 
                  className="h-8 text-xs"
                  placeholder="Greater London"
                />
              </div>
              
              <div>
                <Label htmlFor="postal_code" className="text-xs">Postal Code</Label>
                <Input 
                  id="postal_code"
                  name="postal_code" 
                  value={editedData.postal_code} 
                  onChange={onInputChange} 
                  className="h-8 text-xs"
                  placeholder="SW1A 1AA"
                />
              </div>
              
              <div>
                <Label htmlFor="country" className="text-xs">Country</Label>
                <Input 
                  id="country"
                  name="country" 
                  value={editedData.country} 
                  onChange={onInputChange} 
                  className="h-8 text-xs"
                  placeholder="United Kingdom"
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button 
                  size="sm" 
                  onClick={() => onSave('location')}
                  className="flex-1"
                >
                  <Save className="h-3 w-3 mr-1" />
                  Save
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => onToggleEdit('location')}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {(business.address_line1 || business.city) ? (
                  <div className="text-sm">
                    {business.address_line1 && <div>{business.address_line1}</div>}
                    {business.address_line2 && <div>{business.address_line2}</div>}
                    <div>
                      {[business.city, business.county, business.postal_code]
                        .filter(Boolean)
                        .join(', ')}
                    </div>
                    {business.country && <div>{business.country}</div>}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No address provided</p>
                )}
                
                {business.google_maps_url && (
                  <a 
                    href={business.google_maps_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline flex items-center"
                  >
                    <MapPin className="h-3 w-3 mr-1" />
                    View on Google Maps
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                )}
              </div>
              
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full mt-3" 
                onClick={() => onToggleEdit('location')}
              >
                <Edit className="h-3 w-3 mr-2" />
                Edit Address
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Contact & Social Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center">
            <Phone className="h-5 w-5 mr-2 text-green-600" />
            Contact & Social
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {editingStates.contact ? (
            <form onSubmit={handleContactFormSubmit} className="space-y-3">
              <div>
                <Label htmlFor="contact_phone" className="text-xs">Phone</Label>
                <PhoneInput
                  value={editedData.contact_phone}
                  onChange={handlePhoneChange}
                  onValidationChange={setIsPhoneValid}
                  defaultCountry="GB"
                  placeholder="Enter phone number"
                  className="text-xs"
                />
              </div>
              
              <div>
                <Label htmlFor="contact_email" className="text-xs">Email</Label>
                <Input 
                  id="contact_email"
                  name="contact_email" 
                  type="email"
                  value={editedData.contact_email} 
                  onChange={onInputChange} 
                  className="h-8 text-xs"
                  placeholder="business@example.com"
                />
              </div>
              
              <div>
                <Label htmlFor="website" className="text-xs">Website</Label>
                <Input 
                  id="website"
                  name="website" 
                  type="url"
                  pattern="^https?:\/\/.+"
                  title="Please enter a valid URL starting with http:// or https://"
                  value={editedData.website} 
                  onChange={onInputChange} 
                  className="h-8 text-xs"
                  placeholder="https://www.yourbusiness.com"
                />
              </div>
              
              <div>
                <Label htmlFor="instagram_url" className="text-xs">Instagram</Label>
                <Input 
                  id="instagram_url"
                  name="instagram_url" 
                  type="url"
                  pattern="^https?:\/\/.+"
                  title="Please enter a valid URL starting with http:// or https://"
                  value={editedData.instagram_url} 
                  onChange={onInputChange} 
                  className="h-8 text-xs"
                  placeholder="https://instagram.com/yourbusiness"
                />
              </div>
              
              <div>
                <Label htmlFor="facebook_url" className="text-xs">Facebook</Label>
                <Input 
                  id="facebook_url"
                  name="facebook_url" 
                  type="url"
                  pattern="^https?:\/\/.+"
                  title="Please enter a valid URL starting with http:// or https://"
                  value={editedData.facebook_url} 
                  onChange={onInputChange} 
                  className="h-8 text-xs"
                  placeholder="https://facebook.com/yourbusiness"
                />
              </div>
              
              <div>
                <Label htmlFor="tiktok_url" className="text-xs">TikTok</Label>
                <Input 
                  id="tiktok_url"
                  name="tiktok_url" 
                  type="url"
                  pattern="^https?:\/\/.+"
                  title="Please enter a valid URL starting with http:// or https://"
                  value={editedData.tiktok_url} 
                  onChange={onInputChange} 
                  className="h-8 text-xs"
                  placeholder="https://tiktok.com/@yourbusiness"
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button 
                  type="submit"
                  size="sm" 
                  className="flex-1"
                  disabled={!isPhoneValid}
                >
                  <Save className="h-3 w-3 mr-1" />
                  Save
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    setIsPhoneValid(true);
                    onToggleEdit('contact');
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </form>
          ) : (
            <>
              <div className="space-y-2">
                {business.contact_phone && (
                  <div className="flex items-center text-sm">
                    <Phone className="h-3 w-3 mr-2 text-muted-foreground flex-shrink-0" />
                    <span className="truncate">{business.contact_phone}</span>
                  </div>
                )}
                
                {business.contact_email && (
                  <div className="flex items-center text-sm">
                    <Mail className="h-3 w-3 mr-2 text-muted-foreground flex-shrink-0" />
                    <span className="truncate">{business.contact_email}</span>
                  </div>
                )}
                
                {business.website && (
                  <div className="flex items-center text-sm">
                    <Globe className="h-3 w-3 mr-2 text-muted-foreground flex-shrink-0" />
                    <a 
                      href={business.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline truncate flex items-center"
                    >
                      Visit Website
                      <ExternalLink className="h-3 w-3 ml-1 flex-shrink-0" />
                    </a>
                  </div>
                )}
                
                {business.instagram_url && (
                  <div className="flex items-center text-sm">
                    <Instagram className="h-3 w-3 mr-2 text-muted-foreground flex-shrink-0" />
                    <a 
                      href={business.instagram_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline truncate"
                    >
                      Instagram
                    </a>
                  </div>
                )}
                
                {business.facebook_url && (
                  <div className="flex items-center text-sm">
                    <Facebook className="h-3 w-3 mr-2 text-muted-foreground flex-shrink-0" />
                    <a 
                      href={business.facebook_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline truncate"
                    >
                      Facebook
                    </a>
                  </div>
                )}
                
                {business.tiktok_url && (
                  <div className="flex items-center text-sm">
                    <div className="h-3 w-3 mr-2 text-muted-foreground flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold">TT</span>
                    </div>
                    <a 
                      href={business.tiktok_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline truncate"
                    >
                      TikTok
                    </a>
                  </div>
                )}
                
                {!business.contact_phone && !business.contact_email && !business.website && 
                 !business.instagram_url && !business.facebook_url && !business.tiktok_url && (
                  <p className="text-sm text-muted-foreground">No contact information provided</p>
                )}
              </div>
              
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full mt-3" 
                onClick={() => onToggleEdit('contact')}
              >
                <Edit className="h-3 w-3 mr-2" />
                Edit Contact
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BusinessSidebar;
